import { BpStorage } from '../../util/storage';
import { StorageTypes } from '../../models/util.model';
import { EpUtil } from '../../util/ep-util';
import { EpMetricsEvent } from './models/events.model';
import { EpJsConfig } from '../../models/config.model';
import { BpMetricsApiPaths } from '@ads-ep/api-metrics';
import { BpMetricsContext } from './models/context.model';
import { autoInjectable, singleton } from 'tsyringe';
import { EpLogger } from '../../util/ep-logger';
import { PlacementTrackingObj } from '../../models/placement-data.model';
import { PlacementConfig } from '../../models/placement-config.model';
import { ConfigUtilService } from '../../services/config/config-util.service';
import { QueryParamHelperUtil } from '../../util/query-param-helper/query-param-helper.util';
import { AttributionParams, EpJsAttributionKey } from './models/attribution.model';

@autoInjectable()
@singleton()
export class Analytics extends BpMetricsApiPaths {
  sessionTrackingKey = 'bpstid';
  static trackingIdKey = 'bptid';
  static sessionTrackingKey = 'bpstid';
  static cmpIdKey: 'epcmpid';
  cmpId: string;
  userTrackingId: string;
  sessionTrackingId: string;
  firstImpression = false;

  constructor(private storage?: BpStorage,
              private logger?: EpLogger,
              private configUtilSvc?: ConfigUtilService,
              ) {
    super();
  }

  /*
   * Get all locally stored data from previous
   * visits and store in instance
   * */
  init() {
    this.userTrackingId = this.getLocalUserTrackingId();
    this.sessionTrackingId = this.getSessionTrackingId();
    this.cmpId = this.getCmpId();
    if(this.firstImpression){
      this.storeUrlAttribution();
      this.v1IdentifyUserPost({
        eventProperties: {},
        userProperties:{}
      });
      this.firstImpression = false;
    }
  }

  /*
   * Get the current campaign id from the url or the session.
   * */
  getCmpId(): string {
    const searchStr = QueryParamHelperUtil.getSearchStr();
    let cmpId = QueryParamHelperUtil.getParameterByName('cmp', searchStr);

    if (cmpId) {
      this.storage.setItem(
        StorageTypes.sessionStorage,
        Analytics.cmpIdKey,
        cmpId
      );

    }else {
      cmpId = this.storage.getItem(
        StorageTypes.sessionStorage,
        Analytics.cmpIdKey
      ) || '';
    }
    return cmpId;
  }

  /*
   * Get the current session tracking id or create a new one.
   * If creating a new session tracking id, save it to the session
   * storage.
   * */
  getSessionTrackingId(): string {
    let sessionTrackingId = this.storage.getItem(
      StorageTypes.sessionStorage,
      this.sessionTrackingKey
    );
    if (!sessionTrackingId) {
      this.firstImpression = true;
      sessionTrackingId = EpUtil.generateUUID();
      this.storage.setItem(
        StorageTypes.sessionStorage,
        this.sessionTrackingKey,
        sessionTrackingId
      );
    }
    return sessionTrackingId;
  }

  /*
   * Get user tracking_id if exists. If a tracking ID does not exist,
   * generate one.
   * */
  getLocalUserTrackingId(): string {
    let trackingId = this.storage.getItem(
      StorageTypes.localStorage,
      Analytics.trackingIdKey
    );
    if (!trackingId) {
      trackingId = EpUtil.generateUUID();
      this.saveUserTrackingId(trackingId);
    }
    return trackingId;
  }

  /*
   * Store the user tracking id in local storage for use after the session
   * expires.
   * */
  saveUserTrackingId(userTrackingId: string) {
    this.storage.setItem(
      StorageTypes.localStorage,
      Analytics.trackingIdKey,
      userTrackingId
    );
  }

  getPlacementTrackingObj(placementConfig: PlacementConfig){
    return new PlacementTrackingObj({
      id: placementConfig.id || placementConfig.content.metadata.placementId,
      placementContentId: placementConfig.content.id,
      overlayContentId: placementConfig.content.id
    });
  }

  makeRequest(path: string, eventData: Partial<EpMetricsEvent>) {
    const config = this.configUtilSvc.getConfig();

    eventData.context = new BpMetricsContext(config, {
      userTrackingId: this.userTrackingId,
      sessionTrackingId: this.sessionTrackingId
    });

    const options: RequestInit = {
      body: JSON.stringify(eventData),
      headers: {
        'content-type': 'application/json'
      },
      method: 'POST',
      mode: 'cors'
    };

    if (!config.enableAnalytics) {
      this.logger.log(eventData);
      return;
    }

    const url = `${config.analyticsUrl}${path}`;
    return fetch(url, options);
  }

  /**
   * Intended to be called once per session on SDK load.
   * This method should get the channel and subchannel from
   * the query params and store the values in session storage.
   * */
  storeUrlAttribution(){
    const queryParamAttributes = QueryParamHelperUtil.getAttributionQueryParams();

    const paramsToSave: AttributionParams = {};
    if(queryParamAttributes.channel){
      paramsToSave.channel = queryParamAttributes.channel;
    }
    if(queryParamAttributes.subchannel){
      paramsToSave.subchannel = queryParamAttributes.subchannel;
    }

    const stringifiedParams = JSON.stringify(paramsToSave);

    this.storage.setItem(
      StorageTypes.sessionStorage,
      EpJsAttributionKey,
      stringifiedParams
    );
  }

  /**
   * Retrieve attribution keys from session
   * */
  getSessionAttribution(): AttributionParams{
    const stringifiedParams = this.storage.getItem(
      StorageTypes.sessionStorage,
      EpJsAttributionKey
    );
    let attributionParams: AttributionParams = {};

    if(stringifiedParams) {
      try {
        const tmpParams = JSON.parse(stringifiedParams);
        if (tmpParams?.channel || tmpParams?.subchannel) {
          attributionParams = tmpParams;
        }
      }catch (e){
        this.logger.warn(e);
        return attributionParams;
      }
    }
    return attributionParams;
  }

}
